import { Box, Paper, Typography } from "@mui/material";
import Chart from "react-apexcharts";
import { SIDE_STYLE, toUSD, toUSDShort } from "./negotiation-ui";

const MARKER_SHAPES = ["circle", "square", "diamond"];

// Offer amounts by offer number, one line per side, with the gap between the latest two live offers shaded.
export default function OfferChart({ entries, viewer }) {
  const live = entries.filter((e) => !e.withdrawn_at);
  if (live.length < 2) return null;

  const sides = [];
  const byside = {};
  live.forEach((e, i) => {
    if (!byside[e.side]) {
      byside[e.side] = [];
      sides.push(e.side);
    }
    byside[e.side].push({ x: i + 1, y: Number(e.amount) });
  });
  const nameFor = (side) => {
    if (side === "A" || side === "B" || side === "NEUTRAL") {
      return live.find((e) => e.side === side)?.creator_name || SIDE_STYLE[side].label;
    }
    return `${SIDE_STYLE[side].label}${viewer?.side === side ? " (you)" : ""}`;
  };
  const series = sides.map((s) => ({ name: nameFor(s), data: byside[s] }));

  const [prev, last] = live.slice(-2).map((e) => Number(e.amount));
  const gap = Math.abs(last - prev);
  const annotations =
    gap > 0
      ? {
          yaxis: [
            {
              y: Math.min(prev, last),
              y2: Math.max(prev, last),
              fillColor: "#8a94a6",
              opacity: 0.22,
              borderColor: "#8a94a6",
              label: {
                text: `Gap ${toUSDShort(gap)}`,
                position: "left",
                textAnchor: "start",
                offsetX: 6,
                style: { color: "#2b3445", background: "#e4e8ef", fontSize: "12px", fontWeight: 600 },
              },
            },
          ],
        }
      : {};

  const options = {
    chart: { type: "line", toolbar: { show: false }, animations: { enabled: false }, fontFamily: "inherit" },
    colors: sides.map((s) => SIDE_STYLE[s].hex),
    stroke: { width: 3, curve: "straight" },
    markers: { size: 6, shape: sides.map((_, i) => MARKER_SHAPES[i % MARKER_SHAPES.length]), strokeColors: "#fff", strokeWidth: 2 },
    xaxis: {
      type: "numeric",
      min: 1,
      max: Math.max(live.length, 2),
      tickAmount: Math.max(live.length, 2) - 1,
      decimalsInFloat: 0,
      title: { text: "Offer number" },
    },
    yaxis: {
      decimalsInFloat: 0,
      labels: { formatter: (v) => toUSDShort(v) },
      title: { text: "Amount" },
    },
    tooltip: {
      x: { formatter: (v) => `Offer ${v}` },
      y: { formatter: (v) => toUSD(v) },
    },
    legend: { position: "top", horizontalAlign: "left" },
    grid: { borderColor: "#e6ecf3" },
    annotations,
  };

  return (
    <Paper variant="outlined" className="mt-4 rounded-xl px-4 pt-3 pb-1">
      <Box className="flex flex-wrap items-baseline justify-between gap-2">
        <Typography fontWeight={600}>Offers over time</Typography>
        <Typography fontSize="small" color="text.secondary">
          Withdrawn offers are left out.
        </Typography>
      </Box>
      <Chart options={options} series={series} type="line" height={280} />
    </Paper>
  );
}
